import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { mappingApp } from 'constants/Mapping.constants';
import { current } from 'immer';
import _ from 'lodash';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { TAppName } from 'types/app.type';
import type {
  IAppReducerState,
  IAppService,
  IObjectTypeField,
  IPreviewObjectType,
  IRawObjectType,
  TObjectType,
} from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import {
  type EAppProperties,
  type EApplicationObjectTypeGroup,
  EFieldMappingModes,
  EObjectTypeMappingStatuses,
  type IFieldMapping,
  type IMapping,
  type TField,
  type TType,
  createFieldMapping,
  createObjectTypeMapping,
} from '@timeedit/types';
import api from '../services/api.service';

type TPayload = PayloadAction<{
  appName: TAppName;
  objectTypeIdx?: number;
  fieldIdx?: number;
  fieldLabel?: string;
  fieldExtId?: string;
  type?: string;
  objectTypeFields?: IObjectTypeField[];
  newObj?: TObjectType;
  objectTypeExtId?: string;
  previewObjectType?: IPreviewObjectType;
  dataField?: IObjectTypeField[];
  status?: EObjectTypeMappingStatuses;
  fields?: IFieldMapping[];
  hasChanges?: boolean;
  isActive?: boolean;
  appProperty?: EAppProperties | null;
  isSearchable?: boolean;
  defaultSorting?: boolean;
  applicationObjectTypeLabel?: string;
  applicationObjectTypeGroup?: EApplicationObjectTypeGroup;
  labelField?: string;
  secondaryLabelField?: string;
  data?: IRawObjectType[];
  allocationFieldFromObject?: string;
}>;

const createEmptyAppConfig = (): IAppReducerState => ({
  loading: false,
  hasErrors: false,
  objectTypesMapping: [],
  objectTypeFields: {},
  rawObjectTypes: [],
  previewObjectType: {
    related: [],
    optionalRelated: [],
    fields: {},
  },
});

export const initialState: IAppService = {
  loading: false,
  teExam: createEmptyAppConfig(),
  // TODO: Assessment remote cleanup, remove `teExamV3` here and everywhere else in this slice
  teExamV3: createEmptyAppConfig(),
  teReporting: createEmptyAppConfig(),
  tePreferences: createEmptyAppConfig(),
  hasErrors: false,
  organizationId: '',
  username: '',
  password: '',
  certificateKey: '',
};

type TFetchSettingsSuccessAction = PayloadAction<{
  mapping: IMapping;
  rawObjectTypes?: IRawObjectType[];
}>;

// Slice
const slice = createSlice({
  name: 'appService',
  initialState,
  reducers: {
    // ************* Exam setting **************
    fetchExamSettingsRequest: (state) => {
      isLoadingRequest(state.teExam);
    },
    fetchExamSettingsSuccess: (
      state,
      { payload }: TFetchSettingsSuccessAction
    ) => {
      if (payload.mapping.objectTypes) {
        state.teExam.objectTypesMapping = payload.mapping.objectTypes.map(
          (obj) => {
            const fields = obj.fields.map((field) => {
              const dataConvert = createFieldMapping({
                ...field,
                isActive: true,
              });
              return {
                ...dataConvert,
                isActive: true,
              };
            });
            const objectTypeMappingTypeConvert = createObjectTypeMapping({
              ...obj,
              fields,
            }) as TObjectType;
            return {
              ...objectTypeMappingTypeConvert,
              fieldMappingMode: EFieldMappingModes.MAPPED,
            };
          }
        );
      }
      if (payload?.rawObjectTypes) {
        state.teExam.rawObjectTypes = payload.rawObjectTypes;
      }
      finishedLoadingSuccess(state.teExam);
    },
    fetchExamSettingsFailure: (state) => {
      finishedLoadingFailure(state.teExam);
    },
    // TODO: Assessment remote cleanup, remove the fetchExamV3Mapping* reducers
    fetchExamV3MappingRequest: (state) => {
      isLoadingRequest(state.teExamV3);
    },
    fetchExamV3MappingSuccess: (
      state,
      { payload }: TFetchSettingsSuccessAction
    ) => {
      const currentDataObjectType =
        current(state.teExamV3.objectTypeFields) || {};
      state.teExamV3.objectTypesMapping = payload.mapping.objectTypes.map(
        (obj) => {
          const fields = obj.fields.map((field: any) => {
            const dataConvert = createFieldMapping({
              ...field,
              isActive: true,
            });
            return {
              ...dataConvert,
              isActive: true,
            };
          });
          if (_.get(currentDataObjectType, obj.objectTypeExtId)) {
            const listAllObjField = currentDataObjectType[obj.objectTypeExtId];
            const listCurr = fields.map((i) => i.fieldExtId);
            listAllObjField.forEach((field: IObjectTypeField) => {
              if (!listCurr.includes(field.extid)) {
                fields.push({
                  appFieldName: field.extid,
                  appProperty: null,
                  allocationFieldFromObject: null,
                  fieldExtId: field.extid,
                  fieldLabel: field.extid, // TODO use field name instead
                  isSearchable: false,
                  defaultSorting: false,
                  isActive: false,
                });
              }
            });
          }

          const objectTypeMappingTypeConvert = createObjectTypeMapping({
            ...obj,
            fields,
          });
          return {
            ...objectTypeMappingTypeConvert,
            // fixed for pref
            fieldMappingMode: EFieldMappingModes.FREE_MAPPING,
          } as TObjectType;
        }
      );
      finishedLoadingSuccess(state.teExamV3);
    },
    fetchExamV3MappingFailure: (state) => {
      finishedLoadingFailure(state.teExamV3);
    },
    //  ************* Reporting setting **************
    fetchReportSettingsRequest: (state) => {
      isLoadingRequest(state.teReporting);
    },
    fetchReportSettingsSuccess: (
      state,
      { payload }: TFetchSettingsSuccessAction
    ) => {
      const currentDataObjectType =
        current(state.teReporting.objectTypeFields) || {};
      const dataConvert = payload.mapping.objectTypes.map((obj) => {
        const fields = obj.fields.map((field) => {
          const dataConvert = createFieldMapping({ ...field, isActive: true });
          return {
            ...dataConvert,
            isActive: true,
          };
        });
        if (_.get(currentDataObjectType, obj.objectTypeExtId)) {
          const listAllObjField = currentDataObjectType[obj.objectTypeExtId];
          const listCurr = fields.map((i) => i.fieldExtId);
          listAllObjField.forEach((field: IObjectTypeField) => {
            if (!listCurr.includes(field.extid)) {
              fields.push({
                appFieldName: field.extid,
                appProperty: null,
                allocationFieldFromObject: null,
                fieldExtId: field.extid,
                fieldId: field.id,
                fieldLabel: field.extid,
                isSearchable: false,
                defaultSorting: false,
                isActive: false,
              });
            }
          });
        }

        const objectTypeMappingTypeConvert = createObjectTypeMapping({
          ...obj,
          fields,
        });
        return {
          ...objectTypeMappingTypeConvert,
          // fixed for pref
          fieldMappingMode: EFieldMappingModes.FREE_MAPPING,
        } as TObjectType;
      });
      if (payload?.rawObjectTypes) {
        state.teReporting.rawObjectTypes = payload.rawObjectTypes;
      }
      state.teReporting.objectTypesMapping = dataConvert;
      finishedLoadingSuccess(state.teReporting);
    },
    fetchReportSettingsFailure: (state) => {
      finishedLoadingFailure(state.teReporting);
    },
    //  ************* tePreferences setting **************
    fetchPreferencesSettingsRequest: (state) => {
      isLoadingRequest(state.tePreferences);
    },
    fetchPreferencesSettingsSuccess: (
      state,
      { payload }: TFetchSettingsSuccessAction
    ) => {
      const currentDataObjectType =
        current(state.tePreferences.objectTypeFields) || {};
      state.tePreferences.objectTypesMapping = payload.mapping.objectTypes.map(
        (obj) => {
          const fields = obj.fields.map((field: any) => {
            const dataConvert = createFieldMapping({
              ...field,
              isActive: true,
            });
            return {
              ...dataConvert,
              isActive: true,
            };
          });
          if (_.get(currentDataObjectType, obj.objectTypeExtId)) {
            const listAllObjField = currentDataObjectType[obj.objectTypeExtId];
            const listCurr = fields.map((i) => i.fieldExtId);
            listAllObjField.forEach((field: IObjectTypeField) => {
              if (!listCurr.includes(field.extid)) {
                fields.push({
                  appFieldName: field.extid,
                  appProperty: null,
                  allocationFieldFromObject: null,
                  fieldExtId: field.extid,
                  fieldId: field.id,
                  fieldLabel: field.extid, // TODO use field name instead
                  isSearchable: false,
                  defaultSorting: false,
                  isActive: false,
                });
              }
            });
          }

          const objectTypeMappingTypeConvert = createObjectTypeMapping({
            ...obj,
            fields,
          });
          return {
            ...objectTypeMappingTypeConvert,
            // fixed for pref
            fieldMappingMode: EFieldMappingModes.FREE_MAPPING,
          } as TObjectType;
        }
      );
      finishedLoadingSuccess(state.tePreferences);
    },
    fetchPreferencesSettingsFailure: (state) => {
      finishedLoadingFailure(state.tePreferences);
    },
    updateObjectTypesMapping: (state, { payload }: TPayload) => {
      const { appName, objectTypeIdx, fieldIdx, fieldLabel } = payload;
      state[appName].objectTypesMapping[objectTypeIdx].fields[
        fieldIdx
      ].fieldLabel = fieldLabel;

      state[appName].objectTypesMapping[objectTypeIdx].hasChanges = true;
      if (
        state[appName].objectTypesMapping[objectTypeIdx].status ===
        EObjectTypeMappingStatuses.NOT_MAPPED
      ) {
        state[appName].objectTypesMapping[objectTypeIdx].status =
          EObjectTypeMappingStatuses.MAPPED;
      }
    },
    updateExtIdForFieldInObjectType: (state, { payload }: TPayload) => {
      const { appName, objectTypeIdx, fieldIdx, fieldExtId } = payload;
      state[appName].objectTypesMapping[objectTypeIdx].fields[
        fieldIdx
      ].fieldExtId = fieldExtId;

      state[appName].objectTypesMapping[objectTypeIdx].hasChanges = true;
    },
    updateForObjectType: (state, { payload }: TPayload) => {
      const { appName, objectTypeIdx, ...update } = payload;
      Object.keys(update).forEach((key) => {
        state[appName].objectTypesMapping[objectTypeIdx][key] = update[key];
      });

      state[appName].objectTypesMapping[objectTypeIdx].hasChanges = true;
      if (
        state[appName].objectTypesMapping[objectTypeIdx].status ===
        EObjectTypeMappingStatuses.NOT_MAPPED
      ) {
        state[appName].objectTypesMapping[objectTypeIdx].status =
          EObjectTypeMappingStatuses.MAPPED;
      }
    },
    addNewObjectType: (state, { payload }: TPayload) => {
      const { appName, newObj } = payload;
      state[appName].objectTypesMapping.unshift(newObj);
    },
    fetchObjectTypesFieldsSuccess: (state, { payload }: TPayload) => {
      const { appName, type, objectTypeFields } = payload;
      state[appName].objectTypeFields[type] = objectTypeFields;
      finishedLoadingSuccess(state[appName]);
    },
    fetchTestObjectTypesFieldsSuccess: (state, { payload }: TPayload) => {
      const { appName, objectTypeExtId, previewObjectType } = payload;
      state[appName].previewObjectType[objectTypeExtId] = previewObjectType;
      finishedLoadingSuccess(state[appName]);
    },
    fetchRawObjectFieldsSuccess: (state, { payload }: TPayload) => {
      const { data, appName } = payload;
      state[appName].rawObjectTypes = data;
    },

    updateForFieldInObjectType(state, { payload }: TPayload) {
      const { appName, objectTypeIdx, fieldIdx, ...update } = payload;
      Object.keys(update).forEach((key) => {
        state[appName].objectTypesMapping[objectTypeIdx].fields[fieldIdx][key] =
          (update as Record<string, unknown>)[key];
      });
      state[appName].objectTypesMapping[objectTypeIdx].hasChanges = true;
      if (
        state[appName].objectTypesMapping[objectTypeIdx].status ===
        EObjectTypeMappingStatuses.NOT_MAPPED
      ) {
        state[appName].objectTypesMapping[objectTypeIdx].status =
          EObjectTypeMappingStatuses.MAPPED;
      }
    },
    updateObjectTypeFields(state, { payload }: TPayload) {
      const { appName, dataField, objectTypeExtId } = payload;
      const index = current(state[appName].objectTypesMapping).findIndex(
        (f) => f.objectTypeExtId === objectTypeExtId
      );
      const newData: TObjectType['fields'][number][] = [];
      current(state[appName].objectTypesMapping[index].fields).forEach((f) => {
        if (f.isActive) {
          newData.push(f);
        }
      });
      dataField?.forEach((field) => {
        if (newData.find((f) => f.fieldExtId === field.extid)) {
          return;
        }
        newData.push({
          appFieldName: field.extid,
          appProperty: null,
          allocationFieldFromObject: null,
          fieldExtId: field.extid,
          fieldId: field.id,
          fieldLabel: field.extid, // TODO use field name instead
          defaultSorting: false,
          isSearchable: false,
          isActive: false,
        });
      });
      state[appName].objectTypesMapping[index].fields = newData;
    },
    updateObjectExtIdForObjectType(state, { payload }: TPayload) {
      const { appName, objectTypeIdx, objectTypeExtId, status = '' } = payload;

      const objectTypeId = state[appName].rawObjectTypes.find(
        (obj: any) => obj.extid === objectTypeExtId
      )?.id;

      state[appName].objectTypesMapping[objectTypeIdx].hasChanges = true;
      if (status) {
        state[appName].objectTypesMapping[objectTypeIdx].status = status;
      }
      if (
        state[appName].objectTypesMapping[objectTypeIdx].status ===
        EObjectTypeMappingStatuses.NOT_MAPPED
      ) {
        state[appName].objectTypesMapping[objectTypeIdx].status =
          EObjectTypeMappingStatuses.MAPPED;
      }
      state[appName].objectTypesMapping[objectTypeIdx].objectTypeExtId =
        objectTypeExtId;
      state[appName].objectTypesMapping[objectTypeIdx].objectTypeId =
        objectTypeId;
    },
  },
});

export default slice.reducer;

// exam
export const appServiceExamLoading = (state: TRootState) =>
  state.appService.teExam.loading;

export const appServiceExamObjectTypes = (state: TRootState) =>
  state.appService.teExam.objectTypesMapping;

// TODO: Assessment remote cleanup, remove
export const appServiceExamV3ObjectTypes = (state: TRootState) =>
  state.appService.teExamV3.objectTypesMapping;

// report
export const appServiceReportLoading = (state: TRootState) =>
  state.appService.teReporting.loading;

export const appServiceReportObjectTypes = (state: TRootState) =>
  state.appService.teReporting.objectTypesMapping;

// preferences
export const appServicePreferencesObjectTypes = (state: TRootState) =>
  state.appService.tePreferences.objectTypesMapping;

export const isLoadingObjectType = (appName: TAppName) => (state: TRootState) =>
  state.appService[appName].loading;

export const appServiceRawObjectFields =
  (appName: TAppName, type: string) => (state: TRootState) =>
    state.appService[appName].objectTypeFields[type] ?? [];

export const appServiceObjectTypes =
  (appName: TAppName) => (state: TRootState) =>
    state.appService[appName].objectTypesMapping ?? [];

export const appServiceRawObjectTypes =
  (appName: TAppName) => (state: TRootState) =>
    state.appService[appName].rawObjectTypes ?? [];

// TODO: Need to figure out the proper type, since the usage seems incorrect
export const appPreviewObjectTypes =
  (appName: TAppName, type: string) =>
  (state: TRootState): any => {
    const dataPreview: any =
      _.get(state, `appService.${appName}.previewObjectType`, {}) || {};
    return dataPreview[type] || [];
  };

// Actions
export const {
  // exam
  fetchExamSettingsRequest,
  fetchExamSettingsSuccess,
  fetchExamSettingsFailure,
  // exam v3
  fetchExamV3MappingRequest,
  fetchExamV3MappingSuccess,
  fetchExamV3MappingFailure,
  // report
  fetchReportSettingsRequest,
  fetchReportSettingsSuccess,
  fetchReportSettingsFailure,
  // preferences
  fetchPreferencesSettingsRequest,
  fetchPreferencesSettingsSuccess,
  fetchPreferencesSettingsFailure,
  //
  updateObjectTypesMapping,
  updateExtIdForFieldInObjectType,
  updateForObjectType,
  fetchRawObjectFieldsSuccess,
  //
  fetchObjectTypesFieldsSuccess,
  fetchTestObjectTypesFieldsSuccess,
  updateForFieldInObjectType,
  //
  updateObjectTypeFields,
  updateObjectExtIdForObjectType,
} = slice.actions;

// *************** Exam *******************
export const fetchExamMappingSettings = createAppAsyncThunk(
  'appService/fetchExamMappingSettings',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchExamSettingsRequest());
      const [result, objectsType] = await Promise.all([
        api.get({
          endpoint: `/apps/TE_EXAM/mapping`,
        }),
        api.get({
          endpoint: `/apps/TE_EXAM/integration/objects/types`,
        }),
      ]);
      dispatch(
        fetchExamSettingsSuccess({
          mapping: result,
          rawObjectTypes: objectsType.data,
        })
      );
    } catch (e) {
      dispatch(fetchExamSettingsFailure());
    }
  }
);

// *************** Exam V3 *******************
// TODO: Assessment remote cleanup, remove this thunk
export const fetchExamV3Mapping = createAppAsyncThunk(
  'appService/fetchExamV3Mapping',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchExamV3MappingRequest());
      const result = await api.get({
        endpoint: `/apps/TE_EXAM_V3/mapping`,
      });
      dispatch(fetchExamV3MappingSuccess({ mapping: result }));
    } catch (e) {
      dispatch(fetchExamV3MappingFailure());
      console.error(e);
    }
  }
);

// *************** Report *******************
export const fetchReportMappingSettings = createAppAsyncThunk(
  'appService/fetchReportMappingSettings',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchReportSettingsRequest());

      const [result, objectsType] = await Promise.all([
        api.get({
          endpoint: `/apps/TE_REPORTING/mapping`,
        }),
        api.get({
          endpoint: `/apps/TE_REPORTING/integration/objects/types`,
        }),
      ]);
      dispatch(
        fetchReportSettingsSuccess({
          mapping: result,
          rawObjectTypes: objectsType.data,
        })
      );
    } catch (e) {
      dispatch(fetchReportSettingsFailure());
      console.error(e);
    }
  }
);

// *************** Preferences *******************
export const fetchPreferencesMappingSettings = createAppAsyncThunk(
  'appService/fetchPreferencesMappingSettings',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchPreferencesSettingsRequest());
      const result = await api.get({
        endpoint: `/apps/TE_PREFERENCES/mapping`,
      });
      dispatch(fetchPreferencesSettingsSuccess({ mapping: result }));
    } catch (e) {
      dispatch(fetchPreferencesSettingsFailure());
      console.error(e);
    }
  }
);

export const fetchRawObjectFields = createAppAsyncThunk<void, TAppName>(
  'appService/fetchRawObjectFields',
  async (appName, { dispatch }) => {
    try {
      dispatch(fetchPreferencesSettingsRequest());
      const result = await api.get({
        endpoint: `/types`,
      });

      dispatch(
        fetchRawObjectFieldsSuccess({
          appName,
          data: result.results.map((type: TType) => ({
            extid: type.extId,
            name: type.name,
            id: type.id,
          })),
        })
      );
    } catch (e) {
      dispatch(fetchPreferencesSettingsFailure());
      console.error('errors fetchRawObjectFields', e);
    }
  }
);

type TFetchObjectTypesFields = {
  appName: TAppName;
  type: string;
};

const mappedDispatchStatements = {
  tePreferences: {
    request: fetchPreferencesSettingsRequest,
    success: fetchPreferencesSettingsSuccess,
    failure: fetchPreferencesSettingsFailure,
  },
  // TODO: Assessment remote cleanup, remove
  teExamV3: {
    request: fetchExamV3MappingRequest,
    success: fetchExamV3MappingSuccess,
    failure: fetchExamV3MappingFailure,
  },
};

export const fetchObjectTypesFields = createAppAsyncThunk<
  void,
  TFetchObjectTypesFields
>(
  'appService/fetchObjectTypesFields',
  async ({ appName, type }, { dispatch, getState }) => {
    if (!['tePreferences', 'teExamV3'].includes(appName)) {
      try {
        dispatch(fetchPreferencesSettingsRequest());
        const result = await api.get({
          endpoint: `/apps/${mappingApp[appName]}/integration/objects/types/${type}/fields`,
        });

        dispatch(
          fetchObjectTypesFieldsSuccess({
            appName,
            type,
            objectTypeFields: result.data.map((extid: string) => ({ extid })),
          })
        );
      } catch (err) {
        dispatch(fetchPreferencesSettingsFailure());
        console.error('errors fetchObjectTypesFields', err);
      }
    } else {
      try {
        const store = getState();
        const rawObjectTypes = _.get(
          store,
          `appService.${appName}.rawObjectTypes`
        );
        const typeId = rawObjectTypes.find((i: any) => i.extid === type)?.id;

        if (mappedDispatchStatements?.[appName]) {
          dispatch(mappedDispatchStatements[appName].request());
        }

        const result = await api.post({
          endpoint: '/fields/find',
          suppressNotification: true,
          data: { typeId },
        });

        dispatch(
          fetchObjectTypesFieldsSuccess({
            appName,
            type: type,
            objectTypeFields: result.results.map((field: TField) => ({
              extid: field.extId,
              id: field.id,
            })),
          })
        );
      } catch (err) {
        if (mappedDispatchStatements?.[appName]) {
          dispatch(mappedDispatchStatements[appName].failure());
        }
        console.error('errors fetchObjectTypesFields', err);
      }
    }
  }
);

type FetchTestObjectTypesFields = {
  appName: TAppName;
  type: string;
  objectTypeExtId: string;
};

export const fetchTestObjectTypesFields = createAppAsyncThunk<
  void,
  FetchTestObjectTypesFields
>(
  'appService/fetchTestObjectTypesFields',
  async ({ appName, type, objectTypeExtId }, { dispatch, getState }) => {
    if (!['tePreferences', 'teExamV3'].includes(appName)) {
      try {
        dispatch(fetchPreferencesSettingsRequest());
        const result = await api.get({
          endpoint: `/apps/${mappingApp[appName]}/integration/objects/types/${type}?limit=10`,
        });
        dispatch(
          fetchTestObjectTypesFieldsSuccess({
            appName,
            type,
            objectTypeExtId,
            previewObjectType: result.data,
          })
        );
      } catch (err) {
        dispatch(fetchPreferencesSettingsFailure());
        console.error('errors fetchTestObjectTypesFields', err);
      }
    } else {
      try {
        const store = getState();
        const rawObjectTypes = store.appService[appName].rawObjectTypes;
        const objectType = store.appService[appName].objectTypesMapping.find(
          (item: TObjectType) => item.objectTypeExtId === objectTypeExtId
        );
        const activeFields = objectType?.fields.filter(
          (field) => field.isActive
        );
        const typeId = rawObjectTypes.find(
          (i) => i.extid === objectTypeExtId
        )?.id;

        if (mappedDispatchStatements?.[appName]) {
          dispatch(mappedDispatchStatements[appName].request());
        }

        const fieldsResult = await api.post({
          endpoint: '/fields/find',
          suppressNotification: true,
          data: { typeId },
        });

        const objectsResult = await api.post({
          endpoint: '/objects/find',
          suppressNotification: true,
          data: { typeId, limit: 10 },
        });

        const previewObjectType: IPreviewObjectType = objectsResult.results.map(
          (item: any) => {
            return {
              related: item.related,
              optionalRelated: item.optionalRelated,
              fields: item.fields?.reduce(
                (acc: Record<string, string>, cur: any) => {
                  const fieldExtId = fieldsResult.results.find(
                    (field: any) => cur.fieldId === field.id
                  )?.extId;
                  if (
                    fieldExtId !== undefined &&
                    fieldExtId !== null &&
                    activeFields?.find(
                      (field) => field.fieldExtId === fieldExtId
                    )
                  ) {
                    acc[fieldExtId] = cur.values[0];
                  }
                  return acc;
                },
                {}
              ),
            };
          }
        );

        dispatch(
          fetchTestObjectTypesFieldsSuccess({
            appName,
            type,
            objectTypeExtId,
            previewObjectType,
          })
        );
      } catch (err) {
        if (mappedDispatchStatements?.[appName]) {
          dispatch(mappedDispatchStatements[appName].failure());
        }
        console.error('errors fetchTestObjectTypesFields', err);
      }
    }
  }
);

type TSaveMappingForAppPayload = {
  appName: TAppName;
  objectTypes?: TObjectType[];
};

export const saveMappingFopApp = createAppAsyncThunk<
  void,
  TSaveMappingForAppPayload
>(
  'appService/saveMappingFopApp',
  async ({ appName, objectTypes }, { dispatch, getState }) => {
    try {
      const store = getState();
      let dataBody: TObjectType[];
      if (objectTypes) {
        dataBody = objectTypes;
      } else {
        dataBody = store.appService[appName].objectTypesMapping;
      }
      const dataSave = _.cloneDeep(dataBody).filter((i) => {
        // remove no need
        if (!i.applicationObjectTypeGroup) {
          delete i.applicationObjectTypeGroup;
        }
        delete i.fieldMappingMode;
        delete i.hasChanges;
        if (!i.objectTypeId) {
          i.objectTypeId = store.appService[appName].rawObjectTypes.find(
            (obj: IRawObjectType) => obj.extid === i.objectTypeExtId
          )?.id;
        }

        // in fields
        i.fields = i.fields.filter((f) => f.isActive);
        i.fields.forEach((f) => {
          if (!f.appProperty) f.appProperty = null;
          if (!f.allocationFieldFromObject) f.allocationFieldFromObject = null;
          if (!f.isSearchable) f.isSearchable = false;
          if (!f.defaultSorting) f.defaultSorting = false;
          if (!f.allocationFieldFromObject) f.allocationFieldFromObject = null;
          if (!f.fieldId) {
            const objectTypeFields =
              store?.appService?.[appName]?.objectTypeFields?.[
                i.objectTypeExtId
              ];
            f.fieldId = objectTypeFields?.find((field) => {
              return field.extid === f.fieldExtId;
            })?.id;
          }
          // TODO: We should either remove this or add `fieldMappingMode` to the `IFieldMapping` type
          if ('fieldMappingMode' in f) {
            delete f.fieldMappingMode;
          }
          delete f.isActive;
        });
        return i;
      });

      const result = await api.patch({
        endpoint: `/apps/${mappingApp[appName]}/mapping`,
        data: { objectTypes: dataSave },
      });

      if (mappedDispatchStatements?.[appName]) {
        dispatch(
          mappedDispatchStatements[appName].success({ mapping: result })
        );
      } else if (appName === 'teExam') {
        dispatch(fetchExamSettingsSuccess({ mapping: result }));
      } else if (appName === 'teReporting') {
        dispatch(fetchReportSettingsSuccess({ mapping: result }));
      }
    } catch (err) {}
  }
);

type TTestMappingForObjectType = {
  appName: TAppName;
  objectTypeIdx: number;
  objectType: TObjectType;
};

export const testMappingForObjectType = createAppAsyncThunk<
  void,
  TTestMappingForObjectType
>(
  'appService/testMappingForObjectType',
  async ({ appName, objectTypeIdx, objectType }, { dispatch }) => {
    try {
      await dispatch(saveMappingFopApp({ appName }));
      const result = await api.get({
        endpoint: `/apps/${mappingApp[appName]}/integration/objects/types/${objectType.applicationObjectTypeName}?limit=10`,
      });
      dispatch(
        updateForObjectType({
          appName,
          objectTypeIdx,
          status: result?.data
            ? EObjectTypeMappingStatuses.TESTED
            : EObjectTypeMappingStatuses.FAILED,
        })
      );
      await dispatch(saveMappingFopApp({ appName }));
      dispatch(
        fetchTestObjectTypesFieldsSuccess({
          appName,
          type: objectType.applicationObjectTypeName,
          objectTypeExtId: objectType.objectTypeExtId,
          previewObjectType: result.data,
        })
      );
    } catch (err) {
      dispatch(
        updateForObjectType({
          appName,
          objectTypeIdx,
          status: EObjectTypeMappingStatuses.FAILED,
        })
      );
      dispatch(saveMappingFopApp({ appName }));
    }
  }
);

type TDeleteMappingPayload = {
  appName: TAppName;
  objectTypes: TObjectType[];
  objectTypeIdx: number;
};

export const deleteMapping = createAppAsyncThunk<void, TDeleteMappingPayload>(
  'appService/deleteMapping',
  async ({ appName, objectTypes, objectTypeIdx }, { dispatch }) => {
    try {
      const dataSave = _.cloneDeep(objectTypes);
      dataSave.splice(objectTypeIdx, 1);
      const dataConvert = dataSave.filter((i) => {
        // remove no need
        if (!i.applicationObjectTypeGroup) delete i.applicationObjectTypeGroup;
        delete i.fieldMappingMode;
        delete i.hasChanges;
        // in fields
        i.fields = i.fields.filter((f) => f.isActive);
        i.fields.forEach((f) => {
          if (!f.appProperty) f.appProperty = null;
          if (!f.allocationFieldFromObject) f.allocationFieldFromObject = null;
          if (!f.isSearchable) f.isSearchable = false;
          if (!f.defaultSorting) f.defaultSorting = false;
          if (!f.allocationFieldFromObject) f.allocationFieldFromObject = null;

          // TODO: We should either remove this or add `fieldMappingMode` to the `IFieldMapping` type
          if ('fieldMappingMode' in f) {
            delete f.fieldMappingMode;
          }
          delete f.isActive;
        });
        if (!i.objectTypeExtId) {
          // handle empty case
          i.objectTypeExtId = '_';
        }
        return i;
      });
      const result = await api.patch({
        endpoint: `/apps/${mappingApp[appName]}/mapping`,
        data: { objectTypes: dataConvert },
      });

      if (mappedDispatchStatements?.[appName]) {
        dispatch(
          mappedDispatchStatements[appName].success({ mapping: result })
        );
      } else if (appName === 'teExam') {
        dispatch(fetchExamSettingsSuccess({ mapping: result }));
      } else if (appName === 'teReporting') {
        dispatch(fetchReportSettingsSuccess({ mapping: result }));
      }
    } catch (err) {}
  }
);

type TAddNewObjectTypePayload = {
  appName: TAppName;
  objectType: TObjectType;
  objectTypes: TObjectType[];
};

export const appServiceAddNewObjectType = createAppAsyncThunk<
  void,
  TAddNewObjectTypePayload
>(
  'appService/appServiceAddNewObjectType',
  async ({ appName, objectType, objectTypes }, { dispatch }) => {
    try {
      const newObjectTypes = _.cloneDeep([
        {
          fields: [],
          status: 'NOT_MAPPED',
          ...objectType,
        },
        ...objectTypes,
      ]);

      dispatch(saveMappingFopApp({ appName, objectTypes: newObjectTypes }));
    } catch (err) {}
  }
);
